import classNames from "classnames";
import { AnchorHTMLAttributes, CSSProperties, FC, forwardRef, MouseEvent, PropsWithChildren, ReactNode } from "react";
import SvgOpenInNew from "../icons/OpenInNew";
import Icon from "./Icon";
import styles from "./Menu.module.css";

function getMenuStyle(accentColor?: string): CSSProperties | undefined {
  if (!accentColor) {
    return undefined;
  }

  return { "--menu-accent": accentColor } as CSSProperties;
}

export const MenuHeader: FC<PropsWithChildren> = ({ children }) => <div className={styles.header}>{children}</div>;

export const Divider: FC<{ className?: string }> = ({ className }) => (
  <hr className={classNames(styles.separator, className)} />
);

export type MenuItemProps = {
  icon?: ReactNode;
  label: ReactNode;
  href?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>["target"];
  onClick?: () => void;
  variant?: "default" | "danger";
  className?: string;
};

export const MenuItem: FC<MenuItemProps> = ({ icon, label, href, target, onClick, variant, className }) => {
  const content = (
    <>
      {icon && <span className={styles.icon}>{icon}</span>}
      {label}
      {href && target === "_blank" && (
        <span className={styles.openInNew}>
          <Icon glyph={SvgOpenInNew} />
        </span>
      )}
    </>
  );

  // Menu items are often rendered inside other clickable elements (e.g. a table row's onRowClick),
  // so a click that's actually meant for this item must not bubble up and trigger that too.
  const handleClick = onClick
    ? (event: MouseEvent) => {
        event.stopPropagation();
        onClick();
      }
    : undefined;

  if (href) {
    return (
      <a
        className={classNames(styles.menuItem, variant === "danger" && styles.danger, className)}
        href={href}
        target={target}
        onClick={handleClick}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={classNames(styles.menuItem, variant === "danger" && styles.danger, className)}
      onClick={handleClick}
    >
      {content}
    </button>
  );
};

export type MenuProps = PropsWithChildren<{
  accentColor?: string;
  className?: string;
  style?: CSSProperties;
}>;

export const Menu = forwardRef<HTMLDivElement, MenuProps>(({ accentColor, className, style, children }, ref) => (
  <div
    ref={ref}
    className={classNames(styles.menu, accentColor && styles.accented, className)}
    style={{ ...getMenuStyle(accentColor), ...style }}
  >
    {children}
  </div>
));

Menu.displayName = "Menu";

export default Menu;
